import React, { useContext } from "react";
import PropTypes from "prop-types";
import { Link } from "gatsby";
import useTranslations from "../../hoc/useTranslations";
import { LocaleContext } from "../../components/Layout/Layout";
import NewsletterForm from "../../components/NewsletterForm/NewsletterForm";
import Heading from "../../components/Heading/Heading";
import LinkedinButton from "../../components/LinkedinButton/LinkedinButton";
import { routes } from "../../routes";
import Img from "gatsby-image";
import { useStaticQuery, graphql } from "gatsby";

import EmailSVG from "../../assets/icons/email.svg";

import "./Footer.scss";

const Footer = ({ isPolish }) => {
	const { locale } = useContext(LocaleContext);

	const {
		footer: { title, description, menus },
		meta_data: { funds },
	} = useTranslations();

	const data = useStaticQuery(graphql`
		query {
			ue_1_pl: file(relativePath: { eq: "UE_funds_footer_1.png" }) {
				childImageSharp {
					fluid(maxWidth: 100, quality: 100) {
						...GatsbyImageSharpFluid
					}
				}
			}
			ue_2_pl: file(relativePath: { eq: "UE_funds_footer_2.png" }) {
				childImageSharp {
					fluid(maxWidth: 100, quality: 100) {
						...GatsbyImageSharpFluid
					}
				}
			}
			ue_3_pl: file(relativePath: { eq: "UE_funds_footer_3.png" }) {
				childImageSharp {
					fluid(maxWidth: 100, quality: 100) {
						...GatsbyImageSharpFluid
					}
				}
			}
			ue_4_pl: file(relativePath: { eq: "UE_funds_footer_4.png" }) {
				childImageSharp {
					fluid(maxWidth: 100, quality: 100) {
						...GatsbyImageSharpFluid
					}
				}
			}
			ue_1_eng: file(relativePath: { eq: "UE_funds_footer_ENG_1.png" }) {
				childImageSharp {
					fluid(maxWidth: 100, quality: 100) {
						...GatsbyImageSharpFluid
					}
				}
			}
			ue_2_eng: file(relativePath: { eq: "UE_funds_footer_ENG_2.png" }) {
				childImageSharp {
					fluid(maxWidth: 100, quality: 100) {
						...GatsbyImageSharpFluid
					}
				}
			}
			ue_4_eng: file(relativePath: { eq: "UE_funds_footer_ENG_4.png" }) {
				childImageSharp {
					fluid(maxWidth: 100, quality: 100) {
						...GatsbyImageSharpFluid
					}
				}
			}
		}
	`);

	const { fluid: UE_1_PL } = data.ue_1_pl.childImageSharp;
	const { fluid: UE_2_PL } = data.ue_2_pl.childImageSharp;
	const { fluid: UE_3_PL } = data.ue_3_pl.childImageSharp;
	const { fluid: UE_4_PL } = data.ue_4_pl.childImageSharp;
	const { fluid: UE_1_ENG } = data.ue_1_eng.childImageSharp;
	const { fluid: UE_2_ENG } = data.ue_2_eng.childImageSharp;
	const { fluid: UE_4_ENG } = data.ue_4_eng.childImageSharp;

	const footerMenusLinks = [
		["tel:+48601789982", isPolish ? "mailto:kontakt@botwise.io" : "mailto:contact@botwise.io"],
		[
			routes[locale].blog,
			`${routes[locale].blog}/tag/case-study`,
			routes[locale].customerCareLP,
			routes[locale].privacyPolicy,
			routes[locale].privacyPolicy,
			routes[locale].ueFunds,
			routes[locale].termsConditions,
		],
	];

	const getEUFundsPath = () => {
		return !isPolish ? "/ue-funds" : "/pl/ue-funds";
	};

	const displayLink = (menuIndex, { text, link }) => {
		if (!link) return null;

		switch (menuIndex) {
			case 0: {
				const firstSpaceIndex = text.indexOf(" ");
				const firstPart = text.substring(0, firstSpaceIndex);
				const secondPart = text.substring(firstSpaceIndex);
				return (
					<a href={link} title={text}>
						<span>{firstPart}</span>
						<span className="contact">{secondPart}</span>
					</a>
				);
			}
			case 1:
				return (
					<Link to={link} title={text}>
						{text}
					</Link>
				);
			default:
				return (
					<a href={link} title={text}>
						{text}
					</a>
				);
		}
	};
	return (
		<footer className="footer">
			<div className="footer_content">
				<div className="footer_section--no-margin">
					<div className="footer_form">
						<EmailSVG className="footer__heading--icon" />
						<Heading type={2} className="footer__heading">
							<div className="footer__heading--title">{title}</div>
							<div className="footer__heading--description">{description}</div>
						</Heading>
						<NewsletterForm />
						<div className="footer_subsection footer_desktop">
							<div className="footer__rights">
								<LinkedinButton />
								<span className="footer__rights--text">
									All rights reserved. &copy; BOTWISE {new Date().getFullYear()}
								</span>
							</div>
						</div>
					</div>
				</div>
				<div className="footer_section--no-margin">
					<div className="footer_section">
						{menus.map(({ menu_title, items }, menuIndex) => (
							<div key={menuIndex} className="footer_subsection">
								<div className="divider footer_mobile" />
								<ul className="footer__navigation--container">
									<p className="footer__navigation--title">{menu_title}</p>
									{items.map(({ text }, itemIndex) => (
										<li key={text} className="footer__navigation--item">
											{displayLink(menuIndex, {
												text,
												link: footerMenusLinks[menuIndex][itemIndex],
											})}
										</li>
									))}
								</ul>
								{menuIndex === 0 && (
									<div className="footer__company">
										<p className="footer__company--text">BOTWISE.io Sp. z o.o.</p>
										<p className="footer__company--text">NIP: 8952216674</p>
										<p className="footer__company--text">KRS: 0000834558</p>
										<p className="footer__company--text">REGON: 38582208600000</p>
									</div>
								)}
							</div>
						))}
					</div>
					<div className="footer_subsection footer_mobile">
						<div className="footer__rights">
							<LinkedinButton />
							<span className="footer__rights--text">
								All rights reserved. &copy; BOTWISE {new Date().getFullYear()}
							</span>
						</div>
					</div>
					<div className="footer_subsection">
						<div className="footer__ue">
							<a href={getEUFundsPath()} title={funds.title}>
								<Img fluid={!isPolish ? UE_4_ENG : UE_4_PL} title={funds.title} alt={funds.title} />
							</a>
							<a href={getEUFundsPath()} title={funds.title}>
								<Img fluid={!isPolish ? UE_2_ENG : UE_2_PL} title={funds.title} alt={funds.title} />
							</a>
							<a href={getEUFundsPath()} title={funds.title}>
								<Img fluid={UE_3_PL} title={funds.title} alt={funds.title} />
							</a>
							<a href={getEUFundsPath()} title={funds.title}>
								<Img fluid={!isPolish ? UE_1_ENG : UE_1_PL} title={funds.title} alt={funds.title} />
							</a>
						</div>
					</div>
				</div>
			</div>
		</footer>
	);
};

Footer.propTypes = {
	isPolish: PropTypes.bool.isRequired,
};

export default Footer;
